import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import { School } from 'models/school';
import { SchoolEvent, Participant } from 'models/event';
import { EventDetail } from 'models/eventDetail';
import { PublicEventKitsResponse } from 'models/kitLab';
import { EVENT_TABS, EVENT_STATUS } from 'consts/event';
import { CLUB_EVENT_TABS } from 'consts/club';
import { EVENT_VIEW_TABS } from 'consts/eventView';
import { DEFAULT_SKIP, LIMIT_ALL } from 'consts/table';
import { CLUB_GENDER_SERVER_TO_CLIENT_MAPPING } from 'consts/club';
import {
   getSchoolEvent,
   getSchoolEventDetails,
   getSchoolsPublic,
   getPublicEventKitsForAllSchools,
} from 'services/school';
import {
   getEventGeneratedName,
   getAllSchoolIdsFromEvent,
   getEventParticipants,
   isClubEvent,
   isIndividualSportEvent,
   isTournamentEvent,
   isTeamOrTwoOnTwoSportEvent,
   isEventStatusFinished,
} from 'helpers/event';
import { getAgesGroupFormatted } from 'helpers/accessors';
import { getEventResultForCalendar } from 'helpers/calendar';
import { getDisplayMatchResult } from 'helpers/matchResult';
import { getSchoolEventLocation } from 'helpers/eventView';
import {
   isAthleticSwimmingTournamentSubtype,
   isIndividualTournament,
   isRegularIndividualTournamentSubtype,
} from 'helpers/tournament';
import EventParticipants from './Tabs/EventParticipants';
import EventDetails from './Tabs/EventDetails';
import EventGallery from './Tabs/EventGallery';
import EventMatchReport from './Tabs/EventMatchReport';
import EventFeed from './Tabs/EventFeed';
import EventResults from './Tabs/EventResults';
import Loader from 'components/loader';
import './SchoolEventView.scss';

interface Props { school: School; }

const formatDate = (value: string) => new Intl.DateTimeFormat('en-GB', {
   weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
}).format(new Date(value));

const formatTime = (value?: string) => value ? new Intl.DateTimeFormat('en-GB', {
   hour: '2-digit', minute: '2-digit',
}).format(new Date(value)) : '';

const detailTime = (value?: string) => {
   if (!value) return '';
   const parsed = new Date(value);
   return Number.isNaN(parsed.getTime()) ? value : formatTime(value);
};

const teamId = (team: any) => String(team?.id || team?._id || '');
const scorerTime = (scorer: any) => {
   if (typeof scorer.minute !== 'number') return '';
   return typeof scorer.second === 'number'
      ? `${scorer.minute}:${String(scorer.second).padStart(2, '0')}`
      : `${scorer.minute}′`;
};
const scorerPeriod = (period?: string) => ({
   FIRST_HALF: 'First half', SECOND_HALF: 'Second half', EXTRA_TIME: 'Extra time', SHOOTOUT: 'Shootout',
}[period || ''] || 'Match');

const PublishedTeamMatch = ({ event }: { event: SchoolEvent }) => {
   const match = event.matchResult;
   const display = getDisplayMatchResult(event);
   if (!match || !display || event.resultPublication?.status !== 'PUBLISHED') return null;
   const teams: any[] = event.teamsData?.length ? event.teamsData : event.embeddedTeams || [];
   const schools: any[] = [event.inviterSchool, ...(event.invitedSchools || [])].filter(Boolean);
   const identity = (team: any) => {
      const school = team?.school || schools.find(item => String(item.id) === String(team?.schoolId));
      return { id: teamId(team), name: team?.name || 'Team', school: school?.name || '', logo: school?.pic || '' };
   };
   const home = identity(teams[0]);
   const away = identity(teams[1]);
   const stages = [
      match.halfTime && { label: 'Half-time', score: `${match.halfTime.home}–${match.halfTime.away}` },
      match.extraTime && { label: 'After extra time', score: `${match.extraTime.home}–${match.extraTime.away}` },
      match.shootout && { label: 'Penalties', score: `${match.shootout.home}–${match.shootout.away}` },
   ].filter(Boolean) as Array<{label: string; score: string}>;
   const scorers = match.scorers || [];
   const Team = ({ data }: { data: ReturnType<typeof identity> }) => (
      <div className={`school-event__match-team ${String(match.winnerTeamId) === data.id ? 'is-winner' : ''}`}>
         <span>{data.logo ? <img src={data.logo} alt='' /> : (data.school || data.name).charAt(0)}</span>
         <strong>{data.school || data.name}</strong>
         {data.school && data.name && <small>{data.name}</small>}
         {String(match.winnerTeamId) === data.id && <em>Winner</em>}
      </div>
   );
   return <section className='school-event__match-result'>
      <div className='school-event__match-score'><Team data={home} /><div><small>Full time</small><strong>{display.home}<i>–</i>{display.away}</strong>{display.suffix && <span>{display.suffix}</span>}</div><Team data={away} /></div>
      {stages.length > 0 && <div className='school-event__match-stages'>{stages.map(stage => <span key={stage.label}><small>{stage.label}</small><strong>{stage.score}</strong></span>)}</div>}
      {scorers.length > 0 && <div className='school-event__match-scorers'><h2>Scorers</h2>{scorers.map((scorer, index) => {
         const side = String(scorer.teamId) === home.id ? home : away;
         return <div key={scorer.id || `${scorer.teamId}-${index}`}><span>{scorer.playerNameSnapshot}{scorer.ownGoal ? ' (OG)' : ''}</span><small>{side.school || side.name} · {scorerPeriod(scorer.period)}{scorerTime(scorer) ? ` · ${scorerTime(scorer)}` : ''}</small></div>;
      })}</div>}
   </section>;
};

export default function SchoolEventView({ school }: Props) {
   const navigate = useNavigate();
   const location = useLocation();
   const { id: eventId = '' } = useParams();
   const [event, setEvent] = useState<SchoolEvent>();
   const [eventDetails, setEventDetails] = useState<Partial<EventDetail>>({});
   const [participants, setParticipants] = useState<Participant[]>([]);
   const [eventKitsData, setEventKitsData] = useState<PublicEventKitsResponse | null>(null);
   const [isLoading, setIsLoading] = useState(true);
   const [loadError, setLoadError] = useState(false);
   const [tabIndex, setTabIndex] = useState(0);

   useEffect(() => {
      let active = true;
      setIsLoading(true);
      setLoadError(false);
      let loadedEvent: SchoolEvent;

      getSchoolEvent(school.id, eventId)
         .then(eventResponse => {
            loadedEvent = eventResponse;
            const allSchoolIds = getAllSchoolIdsFromEvent(eventResponse);
            const filter = { where: { id: { $in: allSchoolIds } }, view: { type: 'id_name_pic' }, skip: DEFAULT_SKIP, limit: LIMIT_ALL };
            return Promise.all([
               getSchoolsPublic(filter),
               getPublicEventKitsForAllSchools(school.id, eventResponse.id),
               getSchoolEventDetails(school.id, eventResponse.id).catch(() => ({})),
            ]);
         })
         .then(([schools, kitsData, details]) => {
            if (!active) return;
            setEvent(loadedEvent);
            setParticipants(getEventParticipants(loadedEvent, school, schools));
            setEventKitsData(kitsData as PublicEventKitsResponse);
            setEventDetails(details || {});
         })
         .catch(() => { if (active) setLoadError(true); })
         .finally(() => { if (active) setIsLoading(false); });
      return () => { active = false; };
   }, [school.id, eventId]);

   const tabsToRender = useMemo(() => {
      if (!event) return [];
      const tournament = event.tournament;
      const isIndividual = isIndividualSportEvent(event);
      const isIndividualTournamentEvent = isIndividualTournament(tournament);
      const supportsIndividualResults = isIndividual || isAthleticSwimmingTournamentSubtype(tournament) || isRegularIndividualTournamentSubtype(tournament);
      const baseTabs = isClubEvent(event) ? CLUB_EVENT_TABS : EVENT_TABS.filter(tab => tab !== 'Match report' || (!isTournamentEvent(event) && !isIndividualTournamentEvent));
      return baseTabs.filter(tab => {
         if (!supportsIndividualResults && tab === EVENT_VIEW_TABS.RESULTS) return false;
         return true;
      });
   }, [event, school]);

   useEffect(() => {
      if (!event || tabsToRender.length === 0) return;
      if (isTeamOrTwoOnTwoSportEvent(event)) {
         const index = tabsToRender.findIndex(tab => tab === EVENT_VIEW_TABS.TEAMS);
         if (index >= 0) setTabIndex(index);
      } else if (isIndividualSportEvent(event)) {
         const index = tabsToRender.findIndex(tab => tab === EVENT_VIEW_TABS.RESULTS);
         if (index >= 0) setTabIndex(index);
      }
   }, [event?.id]);

   const goBack = () => {
      const state = location.state as any;
      if (state?.prevPath) navigate(state.prevPath, { state }); else navigate(-1);
   };

   if (isLoading) return <div className='school-event__loading'><Loader /></div>;
   if (loadError || !event) return <main className='school-event'><button className='school-event__back' type='button' onClick={goBack}><ArrowBackRoundedIcon /> Back</button><section className='school-event__error'><ErrorOutlineRoundedIcon /><h1>Event could not be loaded</h1><p>The event may no longer be public, or there may be a temporary problem. Please try again.</p></section></main>;

   const eventName = getEventGeneratedName(event, school.id) || event.sport?.name || 'School event';
   const isCancelled = event.status === EVENT_STATUS.CANCELED;
   const reasonForCancellation = (event.reasonForCancellation || '').trim();
   const reasonForChange = (event.reasonForChange || '').trim();
   const venue = getSchoolEventLocation(event, school.id, school.postcode?.id || '');
   const postcode = event.venue?.postcodeData?.postcode || '';
   const point = event.venue?.point;
   const coordinates = point?.coordinates;
   const mapUrl = coordinates?.length === 2 ? `https://www.google.com/maps/search/?api=1&query=${coordinates[1]},${coordinates[0]}` : '';
   const ages = getAgesGroupFormatted({ ages: [...(event.ages || [])] }, school);
   const gender = CLUB_GENDER_SERVER_TO_CLIENT_MAPPING[event.gender as keyof typeof CLUB_GENDER_SERVER_TO_CLIENT_MAPPING] || event.gender;
   const isFinished = isEventStatusFinished(event);
   const result = isFinished || isCancelled ? getEventResultForCalendar(event, school.id) : '';
   const hasPublishedMatchResult = Boolean(event.matchResult && event.resultPublication?.status === 'PUBLISHED');
   const resultBadge = /^(won|win)$/i.test(result) ? 'W' : /^(lost|loss)$/i.test(result) ? 'L' : /^draw$/i.test(result) ? 'D' : 'FT';
   const activeMapping = eventKitsData?.mappings?.find(mapping => !mapping.schoolId || mapping.schoolId === school.id);
   const kitIds = new Set((activeMapping?.kitLabKits || []).map(mapping => mapping.kitId));
   const kits = (eventKitsData?.kits || []).filter(kit => kitIds.has(kit._id));
   const firstKit = kits[0];

   const currentTab = tabsToRender[tabIndex];
   let tabContent: React.ReactNode = null;
   if (currentTab === EVENT_VIEW_TABS.TEAMS) tabContent = <EventParticipants event={event} participants={participants} activeSchoolId={school.id} eventKitsData={eventKitsData} />;
   if (currentTab === EVENT_VIEW_TABS.RESULTS) tabContent = <EventResults event={event} school={school} />;
   if (currentTab === EVENT_VIEW_TABS.DETAILS) tabContent = <EventDetails event={event} activeSchoolId={school.id} school={school} />;
   if (currentTab === EVENT_VIEW_TABS.GALLERY) tabContent = <EventGallery event={event} activeSchoolId={school.id} />;
   if (currentTab === EVENT_VIEW_TABS.MATCH_REPORT) tabContent = <EventMatchReport event={event} activeSchoolId={school.id} />;
   if (currentTab === EVENT_VIEW_TABS.EVENT_FEED) tabContent = <EventFeed event={event} />;

   return (
      <main className='school-event'>
         <button className='school-event__back' type='button' onClick={goBack}><ArrowBackRoundedIcon />{String((location.state as any)?.prevPath || '').includes('records') ? 'Back' : `Back to ${String((location.state as any)?.prevPath || '').includes('calendar') ? 'calendar' : 'fixtures'}`}</button>
         <article className='school-event__card'>
            <header className='school-event__hero'>
               <div className='school-event__badges'><span>{event.sport?.name || 'School sport'}</span><span className={venue.toLowerCase().startsWith('home') ? 'is-home' : 'is-away'}>{venue.toLowerCase().startsWith('home') ? 'Home' : 'Away'}</span>{isCancelled && <span className='is-cancelled'>Cancelled</span>}<em>{ages}{ages && gender ? ' · ' : ''}{gender}</em></div>
               <h1 className={isCancelled ? 'is-cancelled' : ''}>{eventName}</h1>
               <div className='school-event__essentials'>
                  <span><CalendarMonthRoundedIcon />{formatDate(event.startTime)}</span>
                  <span><ScheduleRoundedIcon />{formatTime(event.startTime)}{event.endTime ? ` – ${formatTime(event.endTime)}` : ''}</span>
                  <span><LocationOnRoundedIcon />{venue}{postcode ? `, ${postcode}` : ''}{mapUrl && <a href={mapUrl} target='_blank' rel='noreferrer'>View map</a>}</span>
               </div>
            </header>

            {isCancelled ? <section className='school-event__notice is-cancelled'><ErrorOutlineRoundedIcon /><div><strong>This event was cancelled</strong><span>{reasonForCancellation ? `Reason given: ${reasonForCancellation}` : 'No cancellation reason was published.'}</span></div></section> : result && !hasPublishedMatchResult ? <section className='school-event__result'><span>{resultBadge}</span><div><strong>{result}</strong><small>Published result</small></div></section> : !hasPublishedMatchResult ? <section className='school-event__summary'>
               {firstKit && <div className='school-event__kit'>{firstKit.segments?.slice(0, 3).map((segment, index) => <i key={`${segment.segmentKey}-${index}`} style={{ backgroundColor: segment.color }} />)}<span><small>Kit</small><strong>{firstKit.name}</strong></span></div>}
               {eventDetails.meetTime && <div><small>Meet time</small><strong>{detailTime(eventDetails.meetTime)}</strong></div>}
               {(eventDetails.teamDeparts || eventDetails.teamReturns) && <div><small>Team departs / returns</small><strong>{detailTime(eventDetails.teamDeparts)}{eventDetails.teamDeparts && eventDetails.teamReturns ? ' / ' : ''}{detailTime(eventDetails.teamReturns)}</strong></div>}
               {mapUrl && <a href={mapUrl} target='_blank' rel='noreferrer'><LocationOnRoundedIcon /> Directions</a>}
            </section> : null}

            {!isCancelled && <PublishedTeamMatch event={event} />}

            {reasonForChange && <section className='school-event__notice'><CalendarMonthRoundedIcon /><div><strong>Latest change</strong><span>{reasonForChange}</span></div></section>}

            <nav className='school-event__tabs' aria-label='Event sections'>
               {tabsToRender.map((tab, index) => <button key={tab} type='button' className={tabIndex === index ? 'is-active' : ''} onClick={() => setTabIndex(index)} aria-current={tabIndex === index ? 'page' : undefined}>{tab === EVENT_VIEW_TABS.GALLERY ? 'Photos & video' : tab}</button>)}
            </nav>
            <section className='school-event__tab-content' role='tabpanel'>{tabContent}</section>
         </article>
      </main>
   );
}
